import React, { useState, useEffect } from 'react'
import { MenuItem, Select, TableCell, Checkbox, ListItemText } from '@mui/material'
import {
  useGetMembersMutation,
  useUpdateTeamMemberAssignUsersMutation
} from 'src/services/white-label-team-management-service'
import toast from 'react-hot-toast'
import ConfirmActionModal from './confirmActionModal'

const TeamSelector = ({ row, setRefetchUser }) => {
  const [getMembers] = useGetMembersMutation()
  const [updateTeamMemberAssignUsers] = useUpdateTeamMemberAssignUsersMutation()
  const [openPlanConfirmModal, setOpenPlanConfirmModal] = useState(false)
  const [teamMembers, setTeamMembers] = useState([])
  const [selectedMemberIds, setSelectedMemberIds] = useState([])
  const [pendingSelection, setPendingSelection] = useState([])
  const [changedMemberId, setChangedMemberId] = useState(null)
  const [isAdding, setIsAdding] = useState(false)

  const handleSelectChange = (event) => {
    const newSelection = event.target.value
    setPendingSelection(newSelection)

    // Determine which member was added or removed
    if (newSelection.length > selectedMemberIds.length) {
      // A member was added
      const addedId = newSelection.find(id => !selectedMemberIds.includes(id))
      setChangedMemberId(addedId)
      setIsAdding(true)
    } else {
      // A member was removed
      const removedId = selectedMemberIds.find(id => !newSelection.includes(id))
      setChangedMemberId(removedId)
      setIsAdding(false)
    }

    handleOpenConfirmModal()
  }

  const handleOpenConfirmModal = () => {
    setOpenPlanConfirmModal(true)
  }

  const handleCloseCofirmModal = () => {
    setOpenPlanConfirmModal(false)
    // Reset pending selection if cancelled
    setPendingSelection([])
  }

  const handleAddOrRemoveTeamMember = async () => {
    try {
      // Apply the pending selection
      setSelectedMemberIds(pendingSelection)

      const requestBody = {
        teamMemberId: changedMemberId,
        userId: row.id,
        isAdding
      }

      const { data } = await updateTeamMemberAssignUsers(requestBody)
      if (data) {
        const actionText = isAdding ? 'added to' : 'removed from'
        const memberName = teamMembers.find(m => m._id === changedMemberId)?.name
        const displayName = memberName ? `${memberName.first} ${memberName.last}` : 'Member'

        toast.success(`${displayName} ${actionText} team successfully`)
        setRefetchUser(true)
      }
    } catch (error) {
      toast.error(`${error?.status || 'Error'}: ${error?.message || 'Failed to Update Team Member!'}`)
      // Restore previous selection on error
      setPendingSelection(selectedMemberIds)
    } finally {
      handleCloseCofirmModal()
    }
  }

  useEffect(() => {
    const fetchMembers = async () => {
      try {
        const { data } = await getMembers({ status: 'accepted' })
        const memberData = data?.data?.docs?.filter((member) => {
          return member.permissions.manageSubUsers === 'Assigned'
        })

        const alreadyMembersList = memberData.filter((member) => {
          return member.permissions.manageSubUsers === 'Assigned' &&
                        member.permissions.assignedSubUsers.includes(row.id)
        })

        setTeamMembers(memberData || [])

        // Set the IDs of already assigned members
        setSelectedMemberIds(alreadyMembersList.map(member => member._id) || [])
      } catch (error) {
        toast.error('Failed to fetch members:', error)
      }
    }

    fetchMembers()
  }, [])

  return (
    <TableCell align='center'>
      <Select
        multiple
        value={selectedMemberIds}
        onChange={handleSelectChange}
        size='small'
        displayEmpty
        renderValue={(selected) => {
          if (selected.length === 0) {
            return 'Select Team'
          }
          if (selected.length === 1) {
            const member = teamMembers.find(m => m._id === selected[0])
            return `${member?.name?.first} ${member?.name?.last}`
          }
          return 'Multiple Members'
        }}
        sx={{
          fontSize: '14px',
          minWidth: '120px',
          '& .MuiSelect-select': {
            padding: '6px 10px'
          }
        }}
      >
        <MenuItem value='' sx={{ fontSize: '14px' }} disabled>
          Select Team
        </MenuItem>
        {teamMembers?.map((member) => (
          <MenuItem
            key={member._id}
            value={member._id}
            sx={{ fontSize: '14px' }}
          >
            <Checkbox checked={selectedMemberIds.indexOf(member._id) > -1} />
            <ListItemText primary={`${member?.name?.first} ${member?.name?.last}`} />
          </MenuItem>
        ))}
      </Select>
      <ConfirmActionModal
        open={openPlanConfirmModal}
        onClose={handleCloseCofirmModal}
        title='Confirmation'
        description={isAdding
          ? 'Are you sure you want to add this member to the team?'
          : 'Are you sure you want to remove this member from the team?'}
        confirmButtonText='Confirm'
        onConfirm={handleAddOrRemoveTeamMember}
      />
    </TableCell>
  )
}

export default TeamSelector
